import { useState } from 'react';
import { READINESS_QUESTIONS } from '../../config/readinessQuestions';
import LearnPopover from './LearnPopover';

const OWEN_AVATAR = '/assets/owen-avatar.png';

// Education for a question: the deeper `learn` content when present,
// otherwise its "why we ask" — every question glows with something.
const learnFor = (qid) => {
  const q = qid && READINESS_QUESTIONS.find((x) => x.id === qid);
  if (!q) return null;
  return q.learn || q.why || null;
};

const DEMO_LEARN = {
  title: "That's exactly it!",
  body: "Whenever my avatar glows like this, I have more to share about that topic — what it means, why lenders care, and what to expect. Click me anytime; it never interrupts your progress.",
};

/**
 * The chat message thread: Owen bubbles, "Owen's take" insight cards, user
 * answers, side questions ("YOUR QUESTION") and side answers
 * ("OWEN · ANSWER"), plus the typing indicator.
 *
 * Question messages whose topic has education content get a glowing,
 * clickable Owen avatar that opens a "learn more" popover to its left.
 */
export default function ChatThread({ msgs, typing }) {
  const [pop, setPop] = useState(null); // { key, rect, title, body }

  const toggleLearn = (key, learn) => (e) => {
    if (pop && pop.key === key) {
      setPop(null);
      return;
    }
    setPop({ key, rect: e.currentTarget.getBoundingClientRect(), ...learn });
  };

  // Only the newest question's avatar animates — older ones stay
  // clickable but calm, so the thread isn't a wall of blinking dots.
  let lastLearnIdx = -1;
  msgs.forEach((m, i) => {
    if (m.kind === 'owen' && learnFor(m.qid)) lastLearnIdx = i;
  });

  return (
    <div className="rdz-chat-thread">
      {msgs.map((m, i) => {
        const learn = m.kind === 'owen' ? learnFor(m.qid) : null;
        return (
        <div className="rdz-msg" key={i}>
          {m.kind === 'owen' && (
            <div className="rdz-msg-owen">
              {learn ? (
                <button
                  type="button"
                  className={'rdz-avatar-btn rdz-learn-anchor' + (i === lastLearnIdx ? '' : ' rdz-avatar-btn--quiet')}
                  onClick={toggleLearn('msg' + i, learn)}
                  aria-label={'Learn more: ' + learn.title}
                  title="Click Owen to learn more"
                >
                  <img src={OWEN_AVATAR} alt="Owen" className="rdz-avatar" />
                </button>
              ) : (
                <img src={OWEN_AVATAR} alt="Owen" className="rdz-avatar" />
              )}
              <div className="rdz-msg-owen__bubble">
                {m.text}
                {m.demo === 'learn' && (
                  <div className="rdz-learn-demo">
                    <button
                      type="button"
                      className="rdz-avatar-btn rdz-learn-anchor"
                      onClick={toggleLearn('demo' + i, DEMO_LEARN)}
                      aria-label="Try it — click the glowing Owen avatar"
                      title="Go ahead — click me"
                    >
                      <img src={OWEN_AVATAR} alt="" className="rdz-avatar" />
                    </button>
                    <span className="rdz-learn-demo__caption">
                      Go ahead — click me to try it. You&apos;ll spot this glow next to questions
                      worth a closer look.
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
          {m.kind === 'insight' && (
            <div className="rdz-msg-insight">
              <div className="rdz-overline rdz-msg-insight__overline">Owen&apos;s take</div>
              <div className="rdz-msg-insight__body">{m.text}</div>
            </div>
          )}
          {m.kind === 'user' && <div className="rdz-msg-user">{m.text}</div>}
          {m.kind === 'sideQ' && (
            <div className="rdz-msg-sideq">
              <span className="rdz-overline rdz-msg-sideq__overline">Your question</span>
              <div className="rdz-msg-sideq__bubble">{m.text}</div>
            </div>
          )}
          {m.kind === 'sideA' && (
            <div className="rdz-msg-sidea">
              <img src={OWEN_AVATAR} alt="Owen" className="rdz-avatar" />
              <div className="rdz-msg-sidea__bubble">
                <div className="rdz-overline rdz-msg-sidea__overline">Owen &middot; answer</div>
                <div className="rdz-msg-sidea__body">{m.text}</div>
              </div>
            </div>
          )}
        </div>
        );
      })}
      {pop && (
        <LearnPopover
          anchorRect={pop.rect}
          title={pop.title}
          body={pop.body}
          onClose={() => setPop(null)}
        />
      )}
      {typing && (
        <div className="rdz-typing">
          <img src={OWEN_AVATAR} alt="Owen typing" className="rdz-avatar" />
          <div className="rdz-typing__bubble">
            <span className="rdz-typing__dot" />
            <span className="rdz-typing__dot" />
            <span className="rdz-typing__dot" />
          </div>
        </div>
      )}
    </div>
  );
}
